import type { AllCountryCodes } from "@teamz/common";
import type { ComponentProps } from "react";

import AddIcon from "@/assets/add.svg";
import CupIcon from "@/assets/cup.svg";
import EditIcon from "@/assets/edit.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { matchAndClubCountryCodes, playerMatchAvailabilities } from "@teamz/common";
import { cva, type VariantProps } from "class-variance-authority";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useUserRole } from "@/hooks/use-user-role";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { PremiumBadge } from "../premium-badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField } from "../ui/text-field";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const emptyButtonVariants = cva(
  "inline-flex h-12 w-full items-center justify-between rounded-xl bg-neutral-50 p-1.5 text-neutral-600",
  {
    variants: {
      variant: {
        "non-available": ["border border-neutral-400 text-neutral-450"],
        "non-confirmed": ["border border-dashed border-neutral-450"],
        "premium": ["border border-neutral-450"],
      },
    },
    defaultVariants: {
      variant: "non-confirmed",
    },
  },
);
type EmptyButtonProps = ComponentProps<"button"> & VariantProps<typeof emptyButtonVariants> & {
  category: string;
  status?: string;
  userRole?: string;
};

function EmptyButton({ category, status, variant, userRole, className, ...props }: EmptyButtonProps) {
  const { t } = useTranslation(["profile"]);
  return (
    <button
      type="button"
      className={cn(
        emptyButtonVariants({ variant }),
        userRole === "club" && variant !== "premium" && "pointer-events-none border-solid",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-1 px-2">
        <CupIcon />
        <span className="text-sm font-medium">{category}</span>
      </div>

      {variant === "premium"
        ? <PremiumBadge />
        : (
            <div className="flex items-center gap-2 px-2">
              <span className={cn("text-sm font-medium", variant === "non-confirmed" ? "text-orange-500" : "text-neutral-450", (userRole === "club" && variant === "non-confirmed") && "text-neutral-500", (userRole === "club" && variant === "non-available") && "text-neutral-450",
              )}
              >
                { userRole !== "club" && status}
                {
                  userRole === "club" && (
                    <>
                      {variant === "non-confirmed" && t("profile:not-confirmed")}
                      {variant === "non-available" && t("profile:not-available")}
                    </>
                  )
                }
              </span>
              {
                userRole !== "club" && (
                  <>
                    {variant === "non-confirmed" && <AddIcon className="size-6 text-orange-500" />}
                    {variant === "non-available" && <EditIcon className="size-5 text-neutral-500" />}
                  </>
                )
              }
            </div>
          )}
    </button>
  );
}

function MatchButton({ player, userRole, className, ...props }: ComponentProps<"button"> & { player: RouterOutputs["player"]["getPlayer"]; userRole?: string }) {
  const actualYear = new Date().getFullYear();
  const { t } = useTranslation(["profile"]);
  const { getCountryName } = useTranslationUtils();

  return (
    <button
      type="button"
      disabled={userRole === "club"}
      className={cn("flex h-24 w-full flex-col rounded-xl border border-neutral-400 p-1.5", className)}
      {...props}
    >
      <div className="flex h-9 w-full items-center justify-between px-2">
        <div className="flex h-9 items-center gap-1 text-neutral-900">
          <CupIcon />
          <span className="text-sm font-medium">{t("profile:match")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-orange-500">{t("profile:available")}</span>
          { userRole !== "club" && <EditIcon className="size-5 text-neutral-500" />}
        </div>
      </div>

      <div className="flex h-12 w-full items-center justify-between px-2">
        <span className="shrink-0 text-sm font-medium text-neutral-700">
          {t("profile:season", { season: actualYear })}
        </span>
        <div className="flex items-center gap-2">
          {matchAndClubCountryCodes.map(code => (
            <Tooltip key={code}>
              <TooltipTrigger asChild>
                <div>
                  <CircleFlag
                    countryCode={code}
                    className={cn("pointer-events-none block size-6 rounded-full border-2 border-white opacity-15 shadow-md", player?.matchAvailabilitiesCountries?.includes(code) && "opacity-100")}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <span>{getCountryName(code.toUpperCase() as AllCountryCodes)}</span>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </button>
  );
}

const updatePlayerMatchAvailabilitiesDU = z.discriminatedUnion("matchAvailabilities", [
  z.object({
    matchAvailabilities: z.literal("not-available"),
    matchAvailabilitiesCountries: z.array(z.enum(matchAndClubCountryCodes), { message: "common:zod.countries" }).length(0),
  }),
  z.object({
    matchAvailabilities: z.literal("available"),
    matchAvailabilitiesCountries: z.array(z.enum(matchAndClubCountryCodes), { message: "common:zod.countries" })
      .min(1, { message: "common:zod.countries" })
      .max(6),
  }),
], { errorMap: () => ({ message: "common:zod.required" }) });
export type UpdatePlayerMatchAvailabilitiesDU = z.infer<typeof updatePlayerMatchAvailabilitiesDU>;

function PlayerMatchForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const { getCountryName } = useTranslationUtils();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateMatchAvailability.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerMatchForm = useForm<UpdatePlayerMatchAvailabilitiesDU>({
    resolver: zodResolver(updatePlayerMatchAvailabilitiesDU),
    defaultValues: {
      matchAvailabilities: player?.matchAvailabilities || undefined,
      matchAvailabilitiesCountries: player?.matchAvailabilitiesCountries || undefined,
    },
  });

  const onSubmit = playerMatchForm.handleSubmit((data) => {
    mutate({
      userId: player.userId,
      matchAvailabilities: data.matchAvailabilities,
      matchAvailabilitiesCountries: data.matchAvailabilitiesCountries,
    });
  });

  return (
    <>
      <SheetForm id="PlayerMatchForm" onSubmit={onSubmit}>
        <Controller
          control={playerMatchForm.control}
          name="matchAvailabilities"
          rules={{
            onChange: () => {
              playerMatchForm.setValue("matchAvailabilitiesCountries", []);
              playerMatchForm.clearErrors();
            },
          }}
          render={({ field, fieldState }) => (
            <RootField>
              <Label>
                {t("profile:availability")}
              </Label>
              <Select
                value={field.value || undefined}
                onValueChange={(value) => {
                  field.onChange(value);
                  playerMatchForm.setValue("matchAvailabilitiesCountries", []);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerMatchAvailabilities.map(availability => (
                      <SelectItem key={availability} value={availability}>
                        {t(`profile:${availability}`)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        <Controller
          control={playerMatchForm.control}
          name="matchAvailabilitiesCountries"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>
                {t("common:availability-countries")}
              </Label>
              <div className="grid grid-cols-2 gap-y-2 sm:grid-cols-3">
                {matchAndClubCountryCodes.map(country => (
                  <Checkbox
                    disabled={!playerMatchForm.watch("matchAvailabilities") || playerMatchForm.watch("matchAvailabilities") === "not-available"}
                    key={country}
                    label={getCountryName(country.toUpperCase() as AllCountryCodes)}
                    checked={field.value?.includes(country)}
                    onCheckedChange={(checked) => {
                      const currentValue = field.value || [];
                      if (checked) {
                        field.onChange([...currentValue, country]);
                      }
                      else {
                        field.onChange(currentValue.filter(c => c !== country));
                      }
                    }}
                  />
                ))}
              </div>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerMatchForm" loading={isPending || playerMatchForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function PlayerMatch({ player }: { player: RouterOutputs["player"]["getPlayer"] }) {
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["profile"]);
  const userRole = useUserRole();
  const { user } = useMe();
  const navigate = useNavigate();

  return (
    <>
      {
        !user.isAdmin && user.plan === "pack_tournoi" && (
          <EmptyButton
            onClick={() => navigate("/plans")}
            variant="premium"
            category={t("profile:match")}
            status={t("profile:not-available")}
            userRole={userRole}
          />
        )
      }
      { !player?.matchAvailabilities && user.plan !== "pack_tournoi"
      && (
        <EmptyButton
          onClick={() => updateModalState("open")}
          variant="non-confirmed"
          category={t("profile:match")}
          status={t("profile:complete")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      ) }

      {player?.matchAvailabilities === "not-available" && user.plan !== "pack_tournoi"
      && (
        <EmptyButton
          onClick={() => updateModalState("open")}
          variant="non-available"
          category={t("profile:match")}
          status={t("profile:not-available")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      )}

      {player?.matchAvailabilities === "available" && user.plan !== "pack_tournoi"
      && (
        <MatchButton
          player={player}
          onClick={() => updateModalState("open")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      )}

      { (userRole !== "club" || user.isAdmin) && (
        <Sheet open={modalState === "open"} onOpenChange={() => updateModalState("close")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {t("profile:availability")}
              </SheetTitle>
            </SheetHeader>

            <PlayerMatchForm player={player} closeModal={() => updateModalState("close")} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
